import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Button,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Menu as MenuIcon, GetAppRounded as GetAppRoundedIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { headerGoldLine, menuPrimaryButtonSx } from "./headerTaasStyles";
import { useTranslation } from "react-i18next";
import { HopeLogo } from "@/shared/branding/TheHopeOnelogo";
import { usePwaInstall } from "@/shared/pwa/usePwaInstall";

interface GuestHeaderProps {
  toggleDrawer: () => void;
  /** When false (mobile lobby + bottom nav), hide the sidebar menu button. */
  showDrawerToggle?: boolean;
}

const GuestHeader: React.FC<GuestHeaderProps> = ({ toggleDrawer, showDrawerToggle = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { t } = useTranslation("header");
  const { canInstall, isIos, promptInstall } = usePwaInstall();
  const [installHelpOpen, setInstallHelpOpen] = useState(false);

  const handleInstallClick = async () => {
    const result = await promptInstall();
    if (result === "ios") {
      setInstallHelpOpen(true);
    }
  };

  const installBtnSx = {
    border: "none",
    color: "var(--primary-800)",
    fontWeight: 600,
    textTransform: "none" as const,
    fontFamily: "var(--primary-font-family)",
    fontSize: isMobile ? "0.8rem" : "0.875rem",
    px: isMobile ? 1.25 : 2,
    py: isMobile ? 0.5 : 0.75,
    borderRadius: "8px",
    gap: 0.75,
    "&:hover": {
      bgcolor: "var(--primary-100)",
    },
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: "var(--primary-100-light)",
          borderBottom: "1px solid var(--primary-200)",
          color: "var(--primary-1000)",
          fontFamily: "var(--primary-font-family)",
          "&::after": {
            ...headerGoldLine,
            top: "auto",
            bottom: 0,
            opacity: 0.6,
          },
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            px: { xs: 1.5, sm: 2.5, md: 4 },
            minHeight: 72,
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* {showDrawerToggle && (
              <IconButton onClick={toggleDrawer} aria-label="Open menu">
                <MenuIcon sx={{ color: "var(--primary-900)" }} />
              </IconButton>
            )} */}
            <HopeLogo size="sm" />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 2 } }}>
            {canInstall && (
              <Button
                variant="text"
                size={isMobile ? "small" : "medium"}
                startIcon={<GetAppRoundedIcon sx={{ fontSize: isMobile ? 16 : 18 }} />}
                onClick={handleInstallClick}
                sx={installBtnSx}
              >
                Install
              </Button>
            )}

            {!isMobile && (
              <Button
                onClick={() => navigate("/sign-in")}
                sx={{
                  fontWeight: 600,
                  color: "var(--primary-700)",
                  textTransform: "none",
                  fontFamily: "var(--primary-font-family)",
                  fontSize: 14,
                  px: 2,
                  "&:hover": {
                    backgroundColor: "var(--primary-100)",
                  },
                }}
              >
                {t("login")}
              </Button>
            )}

            {!isMobile && (
              <Button
                onClick={() => navigate("/register")}
                variant="contained"
                disableElevation
                sx={{
                  background: "var(--button-gradient)",
                  color: "var(--primary-1000)",
                  fontWeight: 600,
                  textTransform: "none",
                  fontFamily: "var(--primary-font-family)",
                  fontSize: 14,
                  px: 3,
                  py: 1,
                  borderRadius: "8px",
                  boxShadow: "var(--shadow-sm)",
                  "&:hover": {
                    background: "var(--button-gradient-hover)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {t("sign_up")}
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Dialog open={installHelpOpen} onClose={() => setInstallHelpOpen(false)}>
        <DialogTitle>Install thehope.one</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.95rem", lineHeight: 1.7, mb: 1.5 }}>
            {isIos
              ? "On iPhone or iPad, add thehope.one to your Home Screen:"
              : "Add thehope.one to your device:"}
          </Typography>
          <Typography component="ol" sx={{ pl: 2.5, m: 0, fontSize: "0.9rem", lineHeight: 1.8 }}>
            {isIos ? (
              <>
                <li>Tap the Share button in Safari</li>
                <li>Scroll and tap Add to Home Screen</li>
                <li>Confirm with Add</li>
              </>
            ) : (
              <>
                <li>Open the browser menu (⋮ or ⋯)</li>
                <li>Choose Install app / Install thehope.one</li>
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setInstallHelpOpen(false)}
            variant="contained"
            disableElevation
            sx={menuPrimaryButtonSx}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GuestHeader;
